/**
 * `shares` procedure group.
 *
 * The load-bearing rule: **a row is written only by its owning org; grants confer
 * READ only.** A recipient who wants to write *adopts*, which forks a tenant-owned
 * copy (same `person_id`, `adopted_from_share_id` provenance, fresh per-tenant
 * enrichment). The live grant survives the adoption so the UI can offer a diff.
 */

import { z } from 'zod';
import { isoDateTimeSchema, nonBlankString, paginationInputSchema, uuidSchema } from './common.ts';
import { contactSchema } from './contacts.ts';
import { sharePrincipalSchema, shareResourceTypeSchema, shareScopeSchema } from './enums.ts';

/**
 * A grant target. Discriminated so an `org` grant can never smuggle a `userId`
 * (the `contact_shares_grantee_ck` CHECK constraint, expressed in the type system).
 */
export const shareGranteeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('org'), orgId: uuidSchema }),
  z.object({ type: z.literal('user'), userId: uuidSchema }),
]);

export const contactShareSchema = z.object({
  id: uuidSchema,
  ownerOrgId: uuidSchema,
  contactId: uuidSchema,
  granteeType: sharePrincipalSchema,
  granteeOrgId: uuidSchema.nullable(),
  granteeUserId: uuidSchema.nullable(),
  scope: shareScopeSchema,
  allowAdopt: z.boolean(),
  expiresAt: isoDateTimeSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  createdBy: uuidSchema,
  createdAt: isoDateTimeSchema,
});

export const listShareSchema = z.object({
  id: uuidSchema,
  ownerOrgId: uuidSchema,
  listId: uuidSchema,
  granteeType: sharePrincipalSchema,
  granteeOrgId: uuidSchema.nullable(),
  granteeUserId: uuidSchema.nullable(),
  scope: shareScopeSchema,
  allowAdopt: z.boolean(),
  expiresAt: isoDateTimeSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  createdBy: uuidSchema,
  createdAt: isoDateTimeSchema,
});

/**
 * One denormalised row for the sharing screens, covering both resource types so the
 * outgoing/incoming lists are a single flat table rather than a client-side union.
 */
export const shareSummarySchema = z.object({
  id: uuidSchema,
  resourceType: shareResourceTypeSchema,
  resourceId: uuidSchema,
  /** Contact display name or list name, resolved server-side. */
  resourceLabel: z.string(),
  ownerOrgId: uuidSchema,
  ownerOrgName: z.string(),
  granteeType: sharePrincipalSchema,
  granteeOrgId: uuidSchema.nullable(),
  granteeOrgName: z.string().nullable(),
  granteeUserId: uuidSchema.nullable(),
  granteeUserEmail: z.string().nullable(),
  scope: shareScopeSchema,
  allowAdopt: z.boolean(),
  /** Number of contacts the grant actually exposes (1 for a contact share). */
  contactCount: z.int().min(0),
  expiresAt: isoDateTimeSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/** An inbound grant, plus whether this tenant has already forked a copy. */
export const incomingShareSchema = shareSummarySchema.extend({
  adoptedContactId: uuidSchema.nullable(),
  adoptedAt: isoDateTimeSchema.nullable(),
});

// --- inputs ------------------------------------------------------------------

export const shareContactCreateInputSchema = z.object({
  contactId: uuidSchema,
  grantee: shareGranteeSchema,
  scope: shareScopeSchema.default('identity'),
  allowAdopt: z.boolean().default(true),
  expiresAt: isoDateTimeSchema.nullish(),
});

export const shareContactRevokeInputSchema = z.object({
  shareId: uuidSchema,
});

export const shareListCreateInputSchema = z.object({
  listId: uuidSchema,
  grantee: shareGranteeSchema,
  scope: shareScopeSchema.default('identity'),
  allowAdopt: z.boolean().default(true),
  expiresAt: isoDateTimeSchema.nullish(),
});

export const shareListRevokeInputSchema = z.object({
  shareId: uuidSchema,
});

export const shareRevokeOutputSchema = z.object({
  ok: z.literal(true),
  shareId: uuidSchema,
  revokedAt: isoDateTimeSchema,
  /** `contact_access` rows removed by the revocation. */
  accessRowsRemoved: z.int().min(0),
});

export const sharesOutgoingInputSchema = paginationInputSchema.extend({
  resourceType: shareResourceTypeSchema.optional(),
  includeRevoked: z.boolean().default(false),
});

export const sharesIncomingInputSchema = paginationInputSchema.extend({
  resourceType: shareResourceTypeSchema.optional(),
  includeRevoked: z.boolean().default(false),
  /** Filter to grants this tenant has not yet forked. */
  unadoptedOnly: z.boolean().default(false),
});

/**
 * `contactId` is required when the share is a *list* share (which contact of the
 * list to fork) and ignored for a contact share. Enforced server-side because a
 * top-level refinement would break path-param extraction for `/shares/{shareId}/adopt`.
 */
export const shareAdoptInputSchema = z.object({
  shareId: uuidSchema,
  contactId: uuidSchema.optional(),
});

export const shareAdoptOutputSchema = z.object({
  contact: contactSchema,
  adoptedFromShareId: uuidSchema,
  /** The fork queues a fresh per-tenant enrichment; nothing is copied from the owner. */
  enrichmentQueued: z.boolean(),
});

// --- invitations -------------------------------------------------------------

/**
 * `share_invitations` — "invite someone who isn't a user yet" (§1.5).
 *
 * ## Three properties of the token, all load-bearing
 *
 * **Only its hash is stored.** `share_invitations.token_hash` is sha256 over the
 * whole token; the plaintext is returned by `shares.invite` EXACTLY ONCE, like
 * the device-pairing key. A database dump is then not a set of live invitations.
 *
 * **The token carries its owner org in the clear.** Not as a secret — the org id
 * is something the inviter already knows — but because accepting has to run
 * under the OWNER org's RLS scope to write a `contact_shares` row, and the
 * accepting user is by definition in a different tenant. Without the prefix the
 * server would have to read the invitation before it knows which scope to read
 * it under, which is a chicken-and-egg the design's own note about this table
 * ("belongs to the system/worker role") sidesteps by pinning the org first. A
 * tampered prefix simply fails the hash lookup.
 *
 * **It expires.** `expiresInHours` is bounded rather than open-ended: an
 * invitation link that works forever is a credential nobody remembers issuing.
 */
export const shareInvitationSchema = z.object({
  id: uuidSchema,
  ownerOrgId: uuidSchema,
  resourceType: shareResourceTypeSchema,
  resourceId: uuidSchema,
  /** Contact display name or list name, resolved server-side. */
  resourceLabel: z.string(),
  inviteeEmail: z.string(),
  scope: shareScopeSchema,
  expiresAt: isoDateTimeSchema,
  acceptedAt: isoDateTimeSchema.nullable(),
  acceptedByUserId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/** 30 days. Long enough for a holiday, short enough to be a link and not a key. */
export const MAX_INVITE_TTL_HOURS = 720;

export const shareInviteInputSchema = z.object({
  resourceType: shareResourceTypeSchema,
  resourceId: uuidSchema,
  /** Where the link is going. Normalized server-side before it is stored. */
  inviteeEmail: nonBlankString(320, 'inviteeEmail'),
  scope: shareScopeSchema.default('identity'),
  allowAdopt: z.boolean().default(true),
  expiresInHours: z.int().min(1).max(MAX_INVITE_TTL_HOURS).default(168),
});

export const shareInviteOutputSchema = z.object({
  invitation: shareInvitationSchema,
  /**
   * The plaintext token, returned EXACTLY ONCE. The server keeps only its
   * sha256; there is no procedure that can show it again.
   */
  token: z.string(),
});

export const shareAcceptInviteInputSchema = z.object({
  token: nonBlankString(512, 'token'),
});

export const shareAcceptInviteOutputSchema = z.object({
  ok: z.literal(true),
  invitationId: uuidSchema,
  resourceType: shareResourceTypeSchema,
  resourceId: uuidSchema,
  scope: shareScopeSchema,
  /** The grant the acceptance created, owned by the inviting org. */
  shareId: uuidSchema,
  /** The accepting user's ACTIVE org — an invitation binds to a tenant, not a person. */
  granteeOrgId: uuidSchema,
  /** `contact_access` rows the fan-out wrote. 1 for a contact, N for a list. */
  contactsGranted: z.int().min(0),
  /**
   * True when this token had already been accepted. The original grant is
   * returned unchanged rather than a 409: a double-click on an emailed link is
   * the normal case, and an error would tell the invitee the link is broken.
   */
  alreadyAccepted: z.boolean(),
});

// --- inferred types ----------------------------------------------------------

export type ShareInvitation = z.output<typeof shareInvitationSchema>;
export type ShareInviteInput = z.input<typeof shareInviteInputSchema>;
export type ShareAcceptInviteOutput = z.output<typeof shareAcceptInviteOutputSchema>;
export type ShareGrantee = z.output<typeof shareGranteeSchema>;
export type ContactShare = z.output<typeof contactShareSchema>;
export type ListShare = z.output<typeof listShareSchema>;
export type ShareSummary = z.output<typeof shareSummarySchema>;
export type IncomingShare = z.output<typeof incomingShareSchema>;
export type ShareContactCreateInput = z.input<typeof shareContactCreateInputSchema>;
export type ShareListCreateInput = z.input<typeof shareListCreateInputSchema>;
